import React, { useContext, useState } from "react";
import PropTypes from "prop-types";
import { Grid, Select, MenuItem, FormControl, FormHelperText } from "@mui/material";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import DateTimeInput from "components/MDDateTimeInput";
import MDButton from "components/MDButton";
import MDBox from "components/MDBox";
import MDAvatar from "components/MDAvatar";
import colors from "assets/theme/base/colors";
import { UserContext, notify } from "App";
import { UploadDevoteeImageAPI } from "APIs/DevoteeAPIs";
import FullScreenLoader from "components/Custom/FullScreenLoader";
// Receipt page theme (shared)
import FormField from "layouts/receipt/components/FormField";
import { useAccent } from "layouts/receipt/components/SectionCard";

const ASSET_BASE = "https://api.minio.loweatelier.com/tuljamaa/static-images/admin-panel";

// Fields shown in the form, in render order. This must not depend on which keys `data` has:
// empty fields can be missing from `data` (e.g. after the draft is restored from
// sessionStorage, JSON drops undefined values) and would otherwise lose their label + input.
const DETAIL_FIELDS = [
  "first_name",
  "middle_name",
  "last_name",
  "email",
  "gender",
  "birth_date",
  "anniversary_date",
  "comments",
];
const DATE_FIELDS = new Set(["birth_date", "anniversary_date"]);
const INPUT_TYPES = { phone: "number", email: "email" };

// Today's date as yyyy-mm-dd in the user's LOCAL time zone
const getTodayISO = () => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
};

// Server format is dd-mm-yyyy, <input type="date"> uses yyyy-mm-dd. The swap is symmetric.
const flipDate = (value) => (value ? `${value}`.split("-").reverse().join("-") : "");

const hasPicture = (picture) => picture && picture[picture.length - 1] !== "/";

const EditDevoteeDetails = ({
  data,
  handleDataChange,
  onPictureUploaded,
  errors,
  requiredFields,
  onFieldBlur,
}) => {
  const userData = useContext(UserContext);
  const { darkMode, accentName } = useAccent();
  const [uploading, setUploading] = useState(false);

  const fullName = [data.first_name, data.last_name].filter(Boolean).join(" ");

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("devotee_id", data.devotee_id);
    formData.append("files", file);
    const input = e.target;
    setUploading(true);
    try {
      const response = await UploadDevoteeImageAPI(formData, userData.access_token);
      // notify(response.message);
      input.value = "";
      await onPictureUploaded?.();
    } finally {
      setUploading(false);
    }
  };

  const handleInputChange = (field) => (e) => {
    const { value } = e.target;
    handleDataChange(field, DATE_FIELDS.has(field) ? flipDate(value) : value);
  };

  const renderField = (field) => {
    const error = errors[field];

    if (field === "gender") {
      return (
        <>
          <FormControl fullWidth error={!!error}>
            <Select
              style={{ width: "100%", height: "45px" }}
              sx={{
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: error ? "red" : undefined,
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: error ? "red" : undefined,
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: error ? "red" : undefined,
                },
              }}
              value={data.gender ?? ""}
              displayEmpty
              onChange={(e) => {
                handleDataChange("gender", e.target.value);
                onFieldBlur?.("gender");
              }}
              onBlur={() => onFieldBlur?.("gender")}
            >
              <MenuItem value="" disabled>
                Select Gender
              </MenuItem>
              <MenuItem value="0">Male</MenuItem>
              <MenuItem value="1">Female</MenuItem>
            </Select>
          </FormControl>
          {error && <FormHelperText error>{error}</FormHelperText>}
        </>
      );
    }

    const isDate = DATE_FIELDS.has(field);
    const value = isDate ? flipDate(data[field]) : data[field] ?? "";
    return (
      <>
        {/* Wrapper forces a red border on DateTimeInput whenever there is an error */}
        <MDBox
          sx={{
            "& .MuiOutlinedInput-notchedOutline, & fieldset": {
              borderColor: error ? "red !important" : undefined,
            },
            "& .MuiInputBase-root, & .MuiOutlinedInput-root": {
              borderColor: error ? "red !important" : undefined,
            },
          }}
        >
          <DateTimeInput
            type={isDate ? "date" : INPUT_TYPES[field] ?? "text"}
            // Greys out future dates in the picker (validation still catches typed dates)
            inputProps={isDate ? { max: getTodayISO() } : undefined}
            autoComplete="off"
            fullWidth
            value={value}
            onChange={handleInputChange(field)}
            onBlur={() => onFieldBlur?.(field)}
            error={!!error}
            placeholder={
              isDate
                ? `Select ${field.split("_").join(" ")}`
                : `Enter ${field.split("_").join(" ")}`
            }
          />
        </MDBox>
        {error && <FormHelperText error>{error}</FormHelperText>}
      </>
    );
  };

  return (
    <Grid container spacing={2}>
      <FullScreenLoader open={uploading} />

      {/* Picture + name (the upload logic is unchanged) */}
      <Grid item xs={12}>
        <MDBox
          display="flex"
          flexDirection={{ xs: "column", sm: "row" }}
          alignItems="center"
          gap={4}
          mb={1}
          sx={{ textAlign: { xs: "center", sm: "left" } }}
        >
          <div style={{ position: "relative", display: "inline-block" }}>
            {/* Same avatar as the list: shows the default person icon when there is no picture */}
            <MDAvatar
              bgColor="primary"
              src={hasPicture(data.picture) ? data.picture : undefined}
              alt="Avatar"
              sx={{
                width: "170px",
                height: "170px",
                border: `3px solid ${colors[accentName].main}`,
              }}
            />
            <MDButton
              color={darkMode ? "dark" : "white"}
              size="small"
              style={{
                width: "35px",
                height: "60px",
                position: "absolute",
                bottom: -15,
                right: -15,
                borderRadius: "100%",
                cursor: "pointer",
              }}
            >
              <img
                style={{ width: "100%" }}
                alt="Edit"
                src={`${ASSET_BASE}/edit-image-${darkMode ? "light" : "dark"}.png`}
              />
            </MDButton>
            <MDInput
              style={{
                width: "50px",
                height: "90px",
                position: "absolute",
                bottom: -40,
                right: 0,
                opacity: 0,
                cursor: "pointer",
                zIndex: 1000,
              }}
              type="file"
              inputProps={{ accept: "image/*" }}
              onChange={handleFileUpload}
            />
          </div>

          <div>
            <MDTypography variant="h4" color={accentName}>
              {fullName}
            </MDTypography>
            {data.email && (
              <MDTypography variant="button" color="text" display="block">
                <a href={`mailto:${data.email}`} style={{ color: "#000000" }}>
                  {data.email}
                </a>
              </MDTypography>
            )}
          </div>
        </MDBox>
      </Grid>

      {DETAIL_FIELDS.map((field) => (
        <FormField key={field} name={field} required={requiredFields.includes(field)}>
          {renderField(field)}
        </FormField>
      ))}
    </Grid>
  );
};

EditDevoteeDetails.propTypes = {
  data: PropTypes.object.isRequired,
  handleDataChange: PropTypes.func.isRequired,
  onPictureUploaded: PropTypes.func,
  errors: PropTypes.objectOf(PropTypes.string),
  requiredFields: PropTypes.arrayOf(PropTypes.string),
  onFieldBlur: PropTypes.func,
};

EditDevoteeDetails.defaultProps = {
  onPictureUploaded: undefined,
  errors: {},
  requiredFields: [],
  onFieldBlur: undefined,
};

export default EditDevoteeDetails;
